import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import { AuthLayoutTitle, Flexed } from '../../styles/GlobalComponents';
import { RouteComponentProps } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button } from '../../components';
import { StrengthBar, StrengthText } from '../Password/PasswordStyled';
import { resetPassword } from './ForgottenPassword.actions';
import { colors } from '../../styles/GlobalStyles';

const captchaId = process.env.REACT_APP_CAPTCHA_ID;

type TParams = { id?: string };

const Password = ({ location, history }: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('onboarding');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [letterUpperCase, setLetterUpperCase] = useState(false);
  const [letterDigit, setLetterDigit] = useState(false);
  const [letterChar, setLetterChar] = useState(false);
  const [passLength, setPassLength] = useState(false);

  const validation = [
    {
      text: t('password.uppercase'),
      strength: 1,
      requirement: letterUpperCase
    },
    {
      text: t('password.special_letter'),
      strength: 2,
      requirement: letterChar
    },
    { text: t('password.number'), strength: 3, requirement: letterDigit },
    { text: t('password.8+character'), strength: 4, requirement: passLength }
  ];
  const upperCase = /^(?=.*?[A-Z])/;
  const digit = /^(?=.*?[0-9])/;
  const specChar = /(?=.*?[#?!@$%^&*-])/;

  const dispatch = useAppDispatch();

  const { loading } = useAppSelector((state) => state.auth);

  const code = location.search.split('=')[1];

  const checkPassword = (e: any) => {
    setPassword(e);

    if (e.match(upperCase)) {
      setLetterUpperCase(true);
    } else {
      setLetterUpperCase(false);
    }

    if (e.match(digit)) {
      setLetterDigit(true);
    } else {
      setLetterDigit(false);
    }

    if (e.match(specChar)) {
      setLetterChar(true);
    } else {
      setLetterChar(false);
    }

    if (e.length >= 8) {
      setPassLength(true);
    } else {
      setPassLength(false);
    }
  };

  const confirmResetPassword = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (password !== password2) {
      ErrorToast(t('forgotten_password_new.passwords_do_not_match'));
    } else if (!password || !password2) {
      ErrorToast(t('forgotten_password_new.passwords_field_is_empty'));
    } else {
      setCaptchaLoading(true);

      window.initGeetest4(
        {
          captchaId,
          language: 'eng',
          product: 'bind',
          riskType: 'slide',
          nativeButton: {
            width: '100%',
            marginBottom: '20px'
          }
        },
        function (captcha: any) {
          captcha.showBox();
          captcha
            .onReady(function () {
              setCaptchaLoading(false);
            })
            .onSuccess(function () {
              const result = captcha.getValidate();
              dispatch(resetPassword({ code, password, result, history }));
            })
            .onError(function () {
              setCaptchaLoading(false);
            });
        }
      );
    }
  };

  return (
    <>
      {loading && <Spinner fullScreen={true} transparent />}
      {captchaLoading && <Spinner fullScreen={true} transparent />}

      <form onSubmit={confirmResetPassword}>
        <AuthLayoutTitle>{t('forgotten_password_new.title')}</AuthLayoutTitle>
        <InputBackOffice
          id={'password'}
          type={'password'}
          placeholder={t('forgotten_password_new.passPlaceholder')}
          name={'password'}
          value={password}
          onChange={checkPassword}
          label={'Password'}
        />
        <InputBackOffice
          id={'password2'}
          type={'password'}
          placeholder={t('forgotten_password_new.confirmPassPlaceholder')}
          name={'password2'}
          value={password2}
          onChange={setPassword2}
          label={'Password'}
        />

        <Flexed between>
          {validation.map((item, index) => {
            return (
              <StrengthBar
                key={index}
                color={
                  password.length > 0
                    ? item.requirement
                      ? colors.success500
                      : colors.error500
                    : colors.neutral400
                }>
                <StrengthText
                  color={
                    password.length > 0
                      ? item.requirement
                        ? colors.success500
                        : colors.error500
                      : colors.neutral400
                  }>
                  {item.text}
                </StrengthText>
              </StrengthBar>
            );
          })}
        </Flexed>

        <Button
          m='50px 0 10px 0'
          type='submit'
          disabled={
            !letterUpperCase ||
            !letterDigit ||
            !letterChar ||
            !passLength ||
            password === '' ||
            password2 === ''
          }>
          {t('password.continue')}
        </Button>
      </form>
    </>
  );
};

export default Password;
